import notifier from '@albert-io/notifier';
import {NestedMarkdown} from 'generic/MarkdownRendererV2/NestedMarkdown.react';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import AudioControls from './AudioControls.react';
import {getMediaErrorMessage, useAudioEvents, useTrackEvents} from './AudioPlayer.utils';
import {useAudioPlayer} from './AudioPlayerProvider.react';
import AudioTrack from './AudioTrack.react';
import {Button, Icon} from '@albert-io/atomic';
import './audio-player.scss';

interface AudioPlayerProps {
  src: string;
  caption?: string;
  onError?: (hasError: boolean, errorMessage: string) => void;
  /** Data attributes spread onto the player's own box, for callers that must address it. */
  containerProps?: Record<`data-${string}`, string>;
}

/**
 * Report audio play promise rejection to Honeybadger
 */
function reportPlayError(error: Error, audio: HTMLAudioElement, src: string, action: string) {
  notifier.notify(error, {
    tags: 'AudioPlayer',
    context: {
      errorType: `PlayPromiseRejection-${action}`,
      errorName: error.name,
      errorMessage: error.message,
      audioSrc: src,
      userAgent: navigator.userAgent,
      readyState: audio.readyState,
      networkState: audio.networkState,
      paused: audio.paused
    }
  });
}

export const AudioPlayer = ({src, caption, onError, containerProps}: AudioPlayerProps) => {
  const {players, registerPlayer, updatePlayerState} = useAudioPlayer();
  const playerState = players[src] || {};
  const {playbackRate, timestamp, duration, isPlaying, pauseFired} = playerState;
  // Chrome can fail a seek to exactly the end of a clip.
  const position = duration > 0 && timestamp >= duration ? 0 : timestamp;
  const [isRegistered, setIsRegistered] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const audioRef = useRef<HTMLAudioElement>(null);
  const trackRef = useRef<HTMLInputElement>(null);
  const playButtonRef = useRef<HTMLButtonElement>(null);
  const hasReportedErrorRef = useRef(false);

  useAudioEvents(audioRef, src, updatePlayerState);
  useTrackEvents(trackRef, hasError);

  useEffect(() => {
    registerPlayer(src);
  }, [src, registerPlayer]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && Object.keys(playerState).length > 0 && !isRegistered) {
      audio.currentTime = position;
      audio.playbackRate = playbackRate;
      setIsRegistered(true);
    }
  }, [playerState]);

  useEffect(() => {
    if (audioRef.current && pauseFired) {
      audioRef.current.pause();
      updatePlayerState(src, {pauseFired: false});
    }
  }, [pauseFired]);

  const handleAudioError = useCallback(
    (event: React.SyntheticEvent<HTMLAudioElement>) => {
      const audio = event.currentTarget;
      const error = audio.error;
      let message = 'Unable to play audio file';

      if (error) {
        switch (error.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            message = 'Audio playback was aborted';
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            message = 'Audio loading failed due to network error';
            break;
          case MediaError.MEDIA_ERR_DECODE:
            message = 'Audio file format is not supported or corrupted';
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            message = 'Audio file format is not supported';
            break;
          default:
            message = 'Unable to play audio file';
        }
      }

      setHasError(true);
      setErrorMessage(message);

      if (onError) {
        onError(true, message);
      }

      if (hasReportedErrorRef.current) return;
      hasReportedErrorRef.current = true;

      notifier.notify(new Error('Audio playback error'), {
        message: 'Audio player failed to load or play file',
        tags: 'AudioPlayer',
        context: {
          errorType: 'MediaError',
          errorCode: error?.code,
          errorLabel: getMediaErrorMessage(error?.code),
          errorMessage: error?.message,
          audioSrc: src,
          userAgent: navigator.userAgent,
          readyState: audio.readyState,
          networkState: audio.networkState,
          paused: audio.paused,
          currentTime: audio.currentTime,
          duration: audio.duration,
          canPlayMP3: audio.canPlayType('audio/mpeg'),
          canPlayOGG: audio.canPlayType('audio/ogg')
        }
      });
    },
    [src, onError]
  );

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    const audio = audioRef.current;
    const currPlayState = players[src].isPlaying;

    if (currPlayState) {
      // Pausing is synchronous and reliable
      audio.pause();
      updatePlayerState(src, {isPlaying: false});
    } else {
      // Optimistically update UI for immediate feedback
      updatePlayerState(src, {isPlaying: true});

      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // ROLLBACK: Revert state on failure
          updatePlayerState(src, {isPlaying: false});
          reportPlayError(error, audio, src, 'togglePlay');
        });
      }
    }
  };

  const seekAudio = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) {
      const seekTime = parseFloat(event.target.value);
      audioRef.current.currentTime = seekTime;
      updatePlayerState(src, {timestamp: seekTime});
    }
  };

  const restartAudio = () => {
    if (!audioRef.current) return;

    const audio = audioRef.current;
    audio.currentTime = 0;
    if (!isPlaying) {
      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            updatePlayerState(src, {isPlaying: true});
          })
          .catch((error) => {
            reportPlayError(error, audio, src, 'restart');
            updatePlayerState(src, {isPlaying: false});
          });
      } else {
        // Fallback for browsers where play() does not return a Promise
        updatePlayerState(src, {isPlaying: true});
      }
    }
  };

  const retryAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;

    flushSync(() => setHasError(false));
    playButtonRef.current?.focus();
    onError?.(false, '');
    updatePlayerState(src, {isPlaying: false});
    audio.load();
    audio.currentTime = position;
    audio.playbackRate = playbackRate;
  };

  const resetErrorReport = () => {
    hasReportedErrorRef.current = false;
  };

  const selectPlaybackRate = (rate: number) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
      updatePlayerState(src, {playbackRate: rate});
    }
  };

  return (
    <div {...containerProps} className='audio-player__container background-subtle'>
      <audio
        ref={audioRef}
        src={src}
        controls={false}
        preload='metadata'
        onError={handleAudioError}
        onLoadedMetadata={resetErrorReport}
      >
        <track kind='captions' />
      </audio>
      {hasError ? (
        <div className='audio-player__error content-negative gap-x1 flex w-full items-center'>
          <Icon icon='triangle-exclamation' />
          {errorMessage}
          <Button className='ml-auto' size='s' variant='outlined' onClick={retryAudio}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className='audio-player__title-track'>
            {caption && <NestedMarkdown className='audio-player__title' text={caption} />}
            <AudioTrack
              duration={duration}
              timestamp={position}
              seekAudio={seekAudio}
              isPlaying={isPlaying}
              trackRef={trackRef}
            />
          </div>
          <AudioControls
            playButtonRef={playButtonRef}
            isPlaying={isPlaying}
            playbackRate={playbackRate}
            togglePlayPause={togglePlayPause}
            restartAudio={restartAudio}
            selectPlaybackRate={selectPlaybackRate}
          />
        </>
      )}
    </div>
  );
};
